// Data e hora deste sistema são SEMPRE de Brasília. Sem exceção, sem "depende
// de onde você está".
//
// O motivo não é preferência de formato: é que cada linha daqui é um fato de
// hospital. "A sonda foi recolocada no dia 11" tem que dizer 11 no celular do
// Ilton, no da Ildete em outro estado, no servidor e no PDF que vai pelo
// WhatsApp. Um relatório que mostra 10 para um e 11 para outro não serve para
// levar ao médico.
//
// Dois defeitos diferentes moram aqui, e por isso são duas famílias de função:
//
// 1. DIA PURO ("2026-09-11"). Não passa por `new Date()` nunca. Meia-noite em
//    UTC é 21h do dia anterior no Brasil — foi assim que a sonda do dia 11
//    apareceu como dia 10 na tela.
// 2. INSTANTE (ISO completo). Passa por `Intl` com `timeZone` FIXO em
//    São Paulo, e não pelo relógio do aparelho de quem abriu a tela.

/** Um fuso, escrito uma vez. Brasília e São Paulo são o mesmo relógio. */
export const FUSO = 'America/Sao_Paulo';

function relogioDeBrasilia(d: Date) {
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: FUSO,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const pega = (t: string) => p.find((x) => x.type === t)!.value;
  return {
    ano: pega('year'), mes: pega('month'), dia: pega('day'),
    // `hourCycle` h23 devolve "24" para meia-noite em alguns runtimes.
    hora: pega('hour') === '24' ? '00' : pega('hour'),
    minuto: pega('minute'), segundo: pega('second'),
  };
}

/**
 * Quanto o relógio de Brasília está adiantado em relação ao UTC, em ms.
 *
 * Calculado, não fixado em -3h: o Brasil já teve horário de verão e pode ter
 * de novo. Quem decide é o banco de fusos do runtime, não uma constante aqui.
 */
function deslocamentoMs(d: Date): number {
  const r = relogioDeBrasilia(d);
  const comoSeFosseUtc = Date.UTC(
    Number(r.ano), Number(r.mes) - 1, Number(r.dia),
    Number(r.hora), Number(r.minuto), Number(r.segundo),
  );
  return comoSeFosseUtc - Math.floor(d.getTime() / 1000) * 1000;
}

// ---------------------------------------------------------------- dia puro
//
// Duas coisas diferentes chegam aqui parecendo iguais, e adivinhar pelo texto
// nao funciona: "2026-09-12T00:00:00.000Z" pode ser o DIA 12 vindo de uma coluna
// DATE do banco, ou as 21h DO DIA 11 vindo de um timestamp. Sao respostas
// opostas para o mesmo texto. Entao quem decide e o chamador, escolhendo a
// funcao — nao uma regra esperta aqui dentro.
//
// Regra: `string` e dia puro (a API ja manda `.slice(0, 10)`), `Date` e
// instante. `diaDoInstante` e a porta para converter um instante em dia.

/** Os dez primeiros caracteres, ao pe da letra. Dia puro nao se converte. */
function soODia(iso: string | Date): string {
  return typeof iso === 'string' ? iso.slice(0, 10) : diaDoInstante(iso);
}

/** "2026-09-11" -> "11/09/2026". */
export function formatarData(iso: string | Date): string {
  const [ano, mes, dia] = soODia(iso).split('-');
  return `${dia}/${mes}/${ano}`;
}

/** "2026-09-11" -> "11/09". Para tabela apertada, onde o ano e obvio. */
export function formatarDiaMes(iso: string | Date): string {
  return formatarData(iso).slice(0, 5);
}

/** Um INSTANTE -> o dia em que ele caiu no calendario de Brasilia. */
export function diaDoInstante(iso: string | Date): string {
  const r = relogioDeBrasilia(typeof iso === 'string' ? new Date(iso) : iso);
  return `${r.ano}-${r.mes}-${r.dia}`;
}

/** Hoje em Brasilia — mesmo que o aparelho esteja com outro fuso. */
export function hojeISO(agora: Date = new Date()): string {
  return diaDoInstante(agora);
}

/** Dias entre dois dias puros. Conta calendario, nao 24h corridas. */
export function diasEntre(de: string | Date, ate: string | Date): number {
  const [a, b] = [soODia(de), soODia(ate)].map((s) => {
    const [ano, mes, dia] = s.split('-').map(Number);
    return Date.UTC(ano!, mes! - 1, dia!);
  });
  return Math.round((b! - a!) / 86_400_000);
}

/** Soma dias a um dia puro, sem passar por fuso nenhum. */
export function somarDias(iso: string | Date, dias: number): string {
  const [ano, mes, dia] = soODia(iso).split('-').map(Number);
  return new Date(Date.UTC(ano!, mes! - 1, dia! + dias)).toISOString().slice(0, 10);
}

// ----------------------------------------------------------------- instante

/** Hora do relógio de Brasília: "14:35". */
export function formatarHora(iso: string | Date): string {
  const r = relogioDeBrasilia(typeof iso === 'string' ? new Date(iso) : iso);
  return `${r.hora}:${r.minuto}`;
}

/** "11/09 14:35" — o formato curto que aparece nas listas. */
export function formatarDataHora(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return `${formatarDiaMes(d)} ${formatarHora(d)}`;
}

/** "11/09/2026 às 14:35" — para cabeçalho e rodapé de documento. */
export function formatarDataHoraLonga(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return `${formatarData(d)} às ${formatarHora(d)}`;
}

/** Agora no formato que o `<input type="datetime-local">` espera, em Brasília. */
export function agoraParaCampo(agora: Date = new Date()): string {
  const r = relogioDeBrasilia(agora);
  return `${r.ano}-${r.mes}-${r.dia}T${r.hora}:${r.minuto}`;
}

/**
 * O que a pessoa digitou no campo ("2026-09-17T14:30") vira o instante certo.
 *
 * `new Date(texto)` usaria o fuso DO APARELHO. Quem registra a medição das 14h30
 * está em Brasília mesmo que o celular ache que não — e a hora gravada é a que
 * vai para o relatório do médico.
 */
export function doCampoParaISO(texto: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(texto);
  if (!m) return new Date(texto).toISOString();
  const [, ano, mes, dia, hora, minuto] = m;
  const comoUtc = Date.UTC(+ano!, +mes! - 1, +dia!, +hora!, +minuto!);
  // Duas passadas: a primeira usa o deslocamento de um instante aproximado, a
  // segunda o corrige caso o chute tenha caído do outro lado de uma virada.
  let real = comoUtc - deslocamentoMs(new Date(comoUtc));
  real = comoUtc - deslocamentoMs(new Date(real));
  return new Date(real).toISOString();
}

/**
 * O que chega na API para um campo de INSTANTE vira o instante certo.
 *
 * Três formas aparecem, e cada uma tem uma leitura:
 * - ISO com fuso ("…Z", "…-03:00"): já é um instante; fica como está.
 * - Data e hora sem fuso ("2026-09-23T14:30"): é hora de Brasília, que é a
 *   única hora que existe aqui.
 * - Só o dia ("2026-09-23"), como vem do laudo importado: vira MEIO-DIA de
 *   Brasília. `new Date("2026-09-23")` daria meia-noite UTC, que é 21h do dia
 *   22 aqui — e o exame apareceria com um dia a menos. Meio-dia fica longe das
 *   duas viradas, e o dia mostrado é o dia do laudo.
 */
export function instanteDeEntrada(valor: string | Date): Date {
  if (valor instanceof Date) return valor;
  const texto = valor.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(texto)) return new Date(doCampoParaISO(`${texto}T12:00`));
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(texto)) return new Date(doCampoParaISO(texto));
  return new Date(texto);
}

/**
 * Idade pelo ano de HOJE EM BRASÍLIA — não pelo ano do servidor, que roda em
 * UTC e vira o ano três horas antes, na noite de 31 de dezembro.
 */
export function idadeEm(anoNascimento: number, agora: Date = new Date()): number {
  return Number(hojeISO(agora).slice(0, 4)) - anoNascimento;
}

/** Um número do jeito que se escreve aqui: 1.234,5 — nunca 1,234.5. */
export function formatarNumero(n: number, casas = 1): string {
  return n.toLocaleString('pt-BR', { maximumFractionDigits: casas });
}
